import { useEffect, useState } from 'react';
import type { ContactoExcluido, Pregunta } from '../../api/ops/asistente';
import type { ResumenCliente } from '../../api/ops/clientes';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Chip } from '../../componentes/Chip';
import { Deshacer } from '../../componentes/Deshacer';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { PanelLateral } from '../../componentes/PanelLateral';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { useCabecera } from '../../marco/cabecera';
import type { PropsApartado } from './comun';
import { autorDe, chipDePregunta, nombreIdioma, TELEFONO_E164, telefonoLimpio } from './reglas';
import { SinRespuesta } from './SinRespuesta';
import { AYUDA, CONSEJOS_INSTRUCCIONES } from './textos';

/**
 * Lo que sabe el asistente (11.6): Preguntas (hasta 200, al borrador), Instrucciones (hasta 4.000, al borrador, con los
 * avisos `instruction_warnings` que orientan y no bloquean, G546) y Sin asistente (clientes o teléfonos E.164, al momento y
 * sin publicar; «Quitar» con «Deshacer» de 5 s; nunca por los últimos números, A3).
 */

const MAX_PREGUNTAS = 200;

export function Preguntas(props: PropsApartado) {
    const { datos, movil } = props;
    const b = datos.borrador.datos;
    const preguntas = b.faqs ?? [];
    const publicadas = datos.publicada === null ? null : (datos.publicada.faqs ?? []);
    const [panel, setPanel] = useState<{ indice: number | null; locale: string; question: string; answer: string; probado: boolean } | null>(null);
    const lleno = preguntas.length >= MAX_PREGUNTAS;

    const abrir = (indice: number | null) => {
        const p = indice === null ? null : (preguntas[indice] ?? null);
        setPanel({ indice, locale: p?.locale ?? b.locales[0] ?? 'es-ES', question: p?.question ?? '', answer: p?.answer ?? '', probado: false });
    };

    // «Responder» una pregunta sin respuesta (G720): el panel de pregunta frecuente con su texto puesto.
    const responder = (texto: string, idioma: string | null) => {
        setPanel({ indice: null, locale: idioma !== null && b.locales.includes(idioma) ? idioma : (b.locales[0] ?? 'es-ES'), question: texto.slice(0, 300), answer: '', probado: false });
    };

    useCabecera({
        ayuda: AYUDA.preguntas,
        acciones: datos.editar && !lleno ? [{ clave: 'anadir', texto: 'Añadir pregunta', icono: 'plus', onClick: () => { abrir(null); } }] : [],
    });

    const errorPregunta = panel !== null && panel.probado && panel.question.trim().length < 3 ? 'Escribe la pregunta.' : null;
    const errorRespuesta = panel !== null && panel.probado && panel.answer.trim() === '' ? 'Escribe la respuesta.' : null;

    const guardar = () => {
        if (panel === null) {
            return;
        }
        if (panel.question.trim().length < 3 || panel.answer.trim() === '') {
            setPanel({ ...panel, probado: true });
            return;
        }
        const nueva: Pregunta = { locale: panel.locale, question: panel.question.trim(), answer: panel.answer.trim() };
        datos.cambiar((c) => {
            const lista = [...(c.faqs ?? [])];
            if (panel.indice === null) {
                lista.push(nueva);
            } else {
                lista[panel.indice] = nueva;
            }
            return { ...c, faqs: lista };
        });
        setPanel(null);
    };

    const quitar = () => {
        if (panel?.indice == null) {
            return;
        }
        const i = panel.indice;
        datos.cambiar((c) => ({ ...c, faqs: (c.faqs ?? []).filter((_, j) => j !== i) }));
        setPanel(null);
    };

    return (
        <div className="rv-ca-apartado">
            <SinRespuesta {...props} onResponder={lleno ? () => undefined : responder} />
            {preguntas.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre="message-square-text" />
                    </span>
                    <strong>Todavía no hay preguntas frecuentes</strong>
                    <span>Añade lo que más te preguntan (aparcamiento, niños, alergias…) y el asistente lo contestará con tus palabras.</span>
                </div>
            ) : (
                <section className="rv-ca-lista" aria-label="Preguntas frecuentes">
                    {preguntas.map((p, i) => {
                        const chip = chipDePregunta(p, publicadas);
                        return (
                            <div key={`${String(i)}-${p.question}`} className="rv-ca-fila-caja">
                                <div className="rv-ca-fila rv-ca-fila--abre" role="button" tabIndex={0} aria-label={`Abrir «${p.question}»`} onClick={() => { abrir(i); }} onKeyDown={(e) => { if (e.key === 'Enter') { abrir(i); } }}>
                                    <span className="rv-ca-fila__texto">
                                        <strong>
                                            {p.question}
                                            {chip !== '' && <Chip estado="pending">{chip}</Chip>}
                                        </strong>
                                        <span>{p.answer}</span>
                                    </span>
                                    {!movil && <span className="rv-as-idioma">{nombreIdioma(p.locale ?? b.locales[0] ?? '')}</span>}
                                    <Icono nombre="chevron-right" className="rv-ca-chevron" />
                                </div>
                            </div>
                        );
                    })}
                </section>
            )}
            <p className="rv-ca-nota rv-ca-nota--gris">{`${String(preguntas.length)} de ${String(MAX_PREGUNTAS)} preguntas. Las nuevas y las cambiadas llegan a tus clientes cuando publicas.`}</p>

            <PanelLateral
                abierto={panel !== null}
                onCerrar={() => { setPanel(null); }}
                movil={movil}
                eyebrow="Pregunta frecuente"
                icono="message-square-text"
                titulo={panel === null ? '' : panel.indice === null ? 'Nueva pregunta frecuente' : (preguntas[panel.indice]?.question ?? '')}
                accion={datos.editar ? (panel?.indice === null ? 'Añadir' : 'Guardar') : ''}
                onAccion={guardar}
            >
                {panel !== null && (
                    <div className="rv-as-panel">
                        <Desplegable etiqueta="Idioma" valor={panel.locale} bloqueado={!datos.editar} opciones={b.locales.map((l) => ({ valor: l, texto: nombreIdioma(l) }))} onChange={(v) => { setPanel({ ...panel, locale: v }); }} />
                        <Campo etiqueta="Pregunta" valor={panel.question} max={300} bloqueado={!datos.editar} error={errorPregunta} mayusculas="sentences" onChange={(v) => { setPanel({ ...panel, question: v }); }} />
                        <Campo etiqueta="Respuesta" valor={panel.answer} max={2000} filas={5} bloqueado={!datos.editar} error={errorRespuesta} ayuda="Con tus palabras: el asistente la adapta al tono." mayusculas="sentences" onChange={(v) => { setPanel({ ...panel, answer: v }); }} />
                        <small className="rv-as-ayuda">Se guarda en el borrador. Tus clientes la verán cuando publiques.</small>
                        {datos.editar && panel.indice !== null && (
                            <Boton tipo="destructivo" icono="trash-2" movil={movil} onClick={quitar}>
                                Quitar esta pregunta
                            </Boton>
                        )}
                    </div>
                )}
            </PanelLateral>
        </div>
    );
}

export function Instrucciones({ datos, navegar }: PropsApartado) {
    const b = datos.borrador.datos;
    const texto = b.instructions ?? '';
    const avisos = datos.guardando ? [] : (b.instruction_warnings ?? []);
    useCabecera({ ayuda: AYUDA.instrucciones, acciones: [] });

    const estado = datos.guardando ? 'Revisando…' : avisos.length > 0 ? `${String(avisos.length)} ${avisos.length === 1 ? 'aviso' : 'avisos'} al revisarlas` : texto.trim() !== '' ? 'Revisadas: no contradicen tus ajustes' : 'Sin instrucciones';

    return (
        <div className="rv-as-config">
            <section className="rv-as-tarjeta rv-as-presenta" aria-label="Instrucciones">
                <div className="rv-as-eyebrow">
                    <span>Información, no órdenes</span>
                    <small>{estado}</small>
                </div>
                <Campo etiqueta="Instrucciones" valor={texto} filas={10} max={4000} bloqueado={!datos.editar} mayusculas="sentences" onChange={(v) => { datos.cambiar((c) => ({ ...c, instructions: v })); }} />
                <small className="rv-as-ayuda rv-as-contador">{`${texto.length.toLocaleString('es-ES')} de 4.000`}</small>
                {avisos.map((a) => (
                    <div key={`${a.code}-${a.excerpt ?? ''}`} className="rv-as-nota rv-as-nota--ambar" role="note">
                        <span>
                            <Icono nombre="triangle-alert" />
                            <span>
                                {a.message}
                                {a.excerpt !== undefined && a.excerpt !== '' && <q className="rv-as-cita">{a.excerpt}</q>}
                            </span>
                        </span>
                        {a.code === 'contradicts_opening_hours' && (
                            <Boton tipo="secundario" icono="clock" onClick={() => { navegar('/negocio?apartado=horario'); }}>
                                Ver el horario
                            </Boton>
                        )}
                    </div>
                ))}
            </section>
            <div className="rv-as-pliegues">
                {CONSEJOS_INSTRUCCIONES.map((t) => (
                    <div key={t.titulo} className="rv-as-tarjeta rv-as-consejo">
                        <span className="rv-as-baldosa">
                            <Icono nombre={t.icono} />
                        </span>
                        <span>
                            <strong>{t.titulo}</strong>
                            <small>{t.texto}</small>
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

interface Alta {
    tipo: 'customer' | 'phone';
    busqueda: string;
    cliente: ResumenCliente | null;
    telefono: string;
    nota: string;
    probado: boolean;
}

export function SinAsistente({ datos, ops, negocio, movil }: PropsApartado) {
    const avisar = useAvisoBreve();
    const [lista, setLista] = useState<ContactoExcluido[] | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [nombres, setNombres] = useState<Record<string, ResumenCliente>>({});
    const [quitando, setQuitando] = useState<string | null>(null);
    const [alta, setAlta] = useState<Alta | null>(null);
    const [resultados, setResultados] = useState<ResumenCliente[]>([]);
    const [ocupado, setOcupado] = useState(false);
    const { fijarExcluidos } = datos;

    useCabecera({
        ayuda: AYUDA['sin-asistente'],
        acciones: datos.editar ? [{ clave: 'anadir', texto: 'Añadir contacto', icono: 'plus', onClick: () => { setAlta({ tipo: 'customer', busqueda: '', cliente: null, telefono: '+34 ', nota: '', probado: false }); } }] : [],
    });

    useEffect(() => {
        let vivo = true;
        ops.listAssistantExcludedContacts(negocio, { limit: 100 })
            .then((r) => {
                if (!vivo) {
                    return;
                }
                setLista(r.data);
                fijarExcluidos(r.data.length);
                for (const c of r.data) {
                    if (c.customer_id != null) {
                        const id = c.customer_id;
                        ops.getCustomer(negocio, id)
                            .then((x) => { if (vivo) { setNombres((n) => ({ ...n, [id]: { id, status: x.datos.status, display_name: x.datos.display_name } })); } })
                            .catch(() => undefined);
                    }
                }
            })
            .catch((e: unknown) => { if (vivo) { setError(e); } });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento, fijarExcluidos]);

    useEffect(() => {
        if (alta?.tipo !== 'customer' || alta.cliente !== null) {
            return;
        }
        let vivo = true;
        const t = setTimeout(() => {
            ops.listCustomers(negocio, { q: alta.busqueda.trim() === '' ? undefined : alta.busqueda.trim(), limit: alta.busqueda.trim() === '' ? 4 : 6 })
                .then((r) => { if (vivo) { setResultados(r.data); } })
                .catch(() => { if (vivo) { setResultados([]); } });
        }, 250);
        return () => {
            vivo = false;
            clearTimeout(t);
        };
    }, [ops, negocio, alta?.tipo, alta?.busqueda, alta?.cliente]);

    if (error !== null) {
        return <EstadoError seccion="la lista sin asistente" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setError(null); setIntento((i) => i + 1); }} movil={movil} />;
    }
    if (lista === null) {
        return <EstadoCargando seccion="la lista sin asistente" />;
    }

    const telefono = alta === null ? '' : telefonoLimpio(alta.telefono);
    const errorCliente = alta?.probado === true && alta.tipo === 'customer' && alta.cliente === null ? 'Elige un cliente.' : null;
    const errorTelefono = alta?.probado === true && alta.tipo === 'phone' && !TELEFONO_E164.test(telefono) ? 'Escribe el teléfono completo, con el prefijo del país.' : null;

    const anadir = async () => {
        if (alta === null) {
            return;
        }
        if ((alta.tipo === 'customer' && alta.cliente === null) || (alta.tipo === 'phone' && !TELEFONO_E164.test(telefono))) {
            setAlta({ ...alta, probado: true });
            return;
        }
        setOcupado(true);
        try {
            const nota = alta.nota.trim() === '' ? {} : { note: alta.nota.trim() };
            const nuevo = await ops.addAssistantExcludedContact(negocio, alta.tipo === 'customer' && alta.cliente !== null ? { customer_id: alta.cliente.id, ...nota } : { phone: telefono, ...nota });
            if (alta.cliente !== null) {
                const c = alta.cliente;
                setNombres((n) => ({ ...n, [c.id]: c }));
            }
            const sin = lista.filter((x) => x.id !== nuevo.id);
            setLista([nuevo, ...sin]);
            fijarExcluidos(sin.length + 1);
            setAlta(null);
        } catch (e) {
            avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido añadir. Inténtalo otra vez.' });
        } finally {
            setOcupado(false);
        }
    };

    const quitar = async (id: string) => {
        setQuitando(null);
        try {
            await ops.removeAssistantExcludedContact(negocio, id);
            const resto = lista.filter((x) => x.id !== id);
            setLista(resto);
            fijarExcluidos(resto.length);
        } catch (e) {
            avisar({ tipo: 'error', texto: avisoBreveDeError(e) ?? 'No se ha podido quitar. Inténtalo otra vez.' });
        }
    };

    const lineaDeAlta = (c: ContactoExcluido) => {
        const dia = new Date(c.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }).replace('.', '');
        const quien = autorDe(c.created_by_member_id, c.created_by_name, datos.equipo);
        return quien === null ? `Añadido el ${dia}` : `Añadido por ${quien} · ${dia}`;
    };
    const nombreDe = (c: ContactoExcluido) => (c.customer_id != null ? (nombres[c.customer_id]?.display_name ?? 'Cliente') : (c.phone ?? ''));
    const lineaDe = (c: ContactoExcluido) =>
        [c.customer_id != null ? (nombres[c.customer_id]?.phone ?? '') : '', c.note ?? ''].filter((x) => x !== '').join(' · ');

    return (
        <div className="rv-ca-apartado">
            <p className="rv-ca-nota rv-ca-nota--gris">El asistente no atiende a estos clientes ni teléfonos: sus mensajes van directos al buzón. Se aplica al momento, sin publicar.</p>
            {lista.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre="user-x" />
                    </span>
                    <strong>El asistente atiende a todos</strong>
                    <span>Si prefieres que a alguien le conteste siempre una persona (un proveedor, un cliente de confianza), añádelo aquí.</span>
                </div>
            ) : (
                <section className="rv-ca-lista" aria-label="Sin asistente">
                    {lista.map((c) => (
                        <div key={c.id} className={`rv-ca-fila-caja${quitando === c.id ? ' rv-ca-fila-caja--apagada' : ''}`}>
                            <div className="rv-ca-fila">
                                <span className="rv-ca-baldosa">
                                    <Icono nombre={c.kind === 'customer' ? 'user' : 'phone'} />
                                </span>
                                <span className="rv-ca-fila__texto">
                                    <strong>{nombreDe(c)}</strong>
                                    {lineaDe(c) !== '' && <span>{lineaDe(c)}</span>}
                                    <span>{lineaDeAlta(c)}</span>
                                </span>
                                {datos.editar && quitando !== c.id && (
                                    <Boton tipo="secundario" icono="x" movil={movil} aria-label={`Quitar a ${nombreDe(c)} de la lista`} onClick={() => { setQuitando(c.id); }}>
                                        Quitar
                                    </Boton>
                                )}
                            </div>
                            {quitando === c.id && (
                                <div className="rv-ca-deshacer">
                                    <Deshacer
                                        clave={c.id}
                                        movil={movil}
                                        texto="Desde su siguiente mensaje le contestará el asistente."
                                        onDeshacer={() => { setQuitando(null); }}
                                        onFin={() => { void quitar(c.id); }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </section>
            )}

            <PanelLateral
                abierto={alta !== null}
                onCerrar={() => { setAlta(null); }}
                movil={movil}
                eyebrow="Sin asistente"
                icono="user-x"
                titulo="Añadir a la lista sin asistente"
                accion="Añadir"
                ocupado={ocupado}
                ocupadoTexto="Añadiendo…"
                onAccion={() => { void anadir(); }}
            >
                {alta !== null && (
                    <div className="rv-as-panel">
                        <div className="rv-as-opciones" role="radiogroup" aria-label="Qué añades">
                            <OpcionRadio elegida={alta.tipo === 'customer'} icono="user" titulo="Un cliente" texto="De tus clientes" onElegir={() => { setAlta({ ...alta, tipo: 'customer', probado: false }); }} />
                            <OpcionRadio elegida={alta.tipo === 'phone'} icono="phone" titulo="Un teléfono" texto="Aunque no sea cliente" onElegir={() => { setAlta({ ...alta, tipo: 'phone', probado: false }); }} />
                        </div>
                        {alta.tipo === 'customer' ? (
                            alta.cliente !== null ? (
                                <div className="rv-as-elegido">
                                    <span>
                                        <strong>{alta.cliente.display_name}</strong>
                                        <small>{[alta.cliente.phone ?? '', alta.cliente.email ?? ''].filter((x) => x !== '').join(' · ')}</small>
                                    </span>
                                    <Boton tipo="fantasma" icono="x" onClick={() => { setAlta({ ...alta, cliente: null }); }}>
                                        Cambiar
                                    </Boton>
                                </div>
                            ) : (
                                <>
                                    <Campo etiqueta="Nombre, teléfono o email" tipo="search" valor={alta.busqueda} error={errorCliente} onChange={(v) => { setAlta({ ...alta, busqueda: v }); }} />
                                    <div className="rv-as-resultados">
                                        {resultados
                                            .filter((r) => !lista.some((x) => x.customer_id === r.id))
                                            .map((r) => (
                                                <button key={r.id} type="button" className="rv-as-resultado" onClick={() => { setAlta({ ...alta, cliente: r }); }}>
                                                    <strong>{r.display_name}</strong>
                                                    <small>{[r.phone ?? '', r.email ?? ''].filter((x) => x !== '').join(' · ')}</small>
                                                </button>
                                            ))}
                                    </div>
                                </>
                            )
                        ) : (
                            <Campo etiqueta="Teléfono con prefijo" tipo="tel" autocompletar="tel" valor={alta.telefono} error={errorTelefono} ayuda="Con el prefijo del país: +34 612 345 678." onChange={(v) => { setAlta({ ...alta, telefono: v }); }} />
                        )}
                        <Campo etiqueta="Nota (opcional)" valor={alta.nota} max={200} ayuda="Para el equipo: por qué no le atiende el asistente." onChange={(v) => { setAlta({ ...alta, nota: v }); }} />
                    </div>
                )}
            </PanelLateral>
        </div>
    );
}
